import {
  createSubproject as createSubprojectService,
  fetchSubprojectDetails,
  fetchSubprojects,
  updateSubprojectDetails as updateSubprojectDetailsService,
  updateSubproject as updateSubprojectService,
} from '@/services/subprojectService';
// import { v4 } from 'uuid'; // No longer needed for client-side ID generation
import { create } from 'zustand';
import { EnergyKind } from '../decarbonization/types';

export interface Subproject {
  id: string;
  projectId: string;
  name: string;
  userSubprojId: string;
  address: string;
  description: string;
  sector: string;
  sponsoringDepts: string[];
  overseeingDepts: string[];
  usingDepts: string[];
  programManagers: string[];
  projectManagers: string[];
  budgetOfficers: string[];
  projectShape?: string | null;
  stage1Status?: 'not-started' | 'in-progress' | 'finished';
  stage2Status?: 'not-started' | 'in-progress' | 'finished';
  stage3Status?: 'not-started' | 'in-progress' | 'finished';
  pencilsDown?: boolean;
  pencilsDownDate?: string;
  pencilsDownBy?: number;
}

export interface Area {
  value: number;
  unit: string;
}

export interface BuildingParameters {
  enabled: boolean;
  conditionedSpace: Area;
  perviousArea: Area;
  propertySize: Area;
  buildingFootprint: Area;
  numberOfFloors: number;
  numberOfOccupants: number;
  // Phase 5 BLDG requirements
  designLife?: number | null; // BLDG-03: Story A.5 - overrides project-level default (years)
  maintenanceCycle?: number | null; // BLDG-04: Story A.5 - major maintenance cycle period (years)
  constructionType?: 'new' | 'renewal'; // BLDG-06: source-csv.csv line 117
}

export interface TechnologyParameter {
  techId: string;
  units: number;
}

// TODO(cleanup PR): duplicate of ExternalEnergyParam in decarbonization/types/energy.ts.
// Unify — delete this and import ExternalEnergyParam, pointing ExternalEnergy.params at it.
// Note the local ExternalEnergy interface below collides by name with the one in
// types/energy.ts, so the import needs an alias/rename. Isolated to external energy;
// onsite/HVAC/lighting/specialized use TechnologyParameter and are unaffected.
interface EnergyParameter {
  utilityIdentifier: string;
  ratePlanType: 'electric' | 'gas';
  state: string;
  sector: string;
  MWh: number;
}

export interface SubprojectTechnology {
  technologyCategory: string;
  params: TechnologyParameter[];
  costs: {
    capitalCost: number;
    maintenanceCost: number;
    energyCost: number;
    replacementCosts?: number;
  };
  energyDemand: Record<EnergyKind, number>;
  demoCosts: {
    capitalCost: number;
    maintenanceCost: number;
    replacementCosts?: number;
  };
  demoEnergyDemand: Record<EnergyKind, number>;
}

export interface BuildingCosts {
  capitalCost: number;
  maintenanceCost: number;
  replacementCosts?: number;
  majorMaintenanceCost?: number;
  regionalMultiplier?: number;
  // Per-category annual O&M for on-site energy tech (Generators, Solar, Wind, ...).
  // One annual pie wedge each; maintenance only (generation is credited into energy cost).
  onsiteCosts?: Record<string, number>;
}

export interface OnSiteEnergy {
  params: TechnologyParameter[];
}

export interface OnSiteCostsTechnology {
  capitalCost: number,
  maintenanceCost: number,
  name: string
}

export interface OnSiteCosts {
  capitalCost: number;
  maintenanceCost: number;
  majorMaintenanceCost: number;
  regionalMultiplier: number;
  technology: Record<string, OnSiteCostsTechnology>;
}

export interface ExternalEnergy {
  params: EnergyParameter[];
}

export interface BuildingAlternative {
  buildingParams: BuildingParameters;
  technologyParams: Record<string, SubprojectTechnology>;
  onSiteEnergy: OnSiteEnergy;
  onSiteCosts: OnSiteCosts;
  externalEnergy: ExternalEnergy;
  costs: BuildingCosts;
  energyDemand: Record<EnergyKind, number>;
  energyOnsiteSupply: Record<EnergyKind, number>;
  energyExternalDemand: Record<EnergyKind, number>;
  externalEnergyCosts: Record<string, number>;
  tonnesCo2: number;
  demoFudgeCosts: BuildingCosts;
  demoFudgeEnergyDemand: Record<EnergyKind, number>;
}

export interface SubprojectBuildingDetails extends Subproject {
  buildingUsages: string[];
  airExchangeFactor: number;
  enabledTechnologyCategories: string[];
  conventionalAlternative: BuildingAlternative;
  reducedCarbonAlternative: BuildingAlternative;
  zeroCarbonAlternative: BuildingAlternative;
  designCost: number | null;
  constructionCost: number | null;
  newOrRenewal: 'new' | 'renewal' | null;
  designLifeYears: number | null;
  majorMaintenanceCost: number | null;
  maintenanceCycle: number | null;
}

interface SubprojectStore {
  /**
   * Fetch subprojects for a project, optionally scoped to an organization.
   */
  fetchSubprojects: (
    projectId: string,
    organizationId?: string,
  ) => Promise<SubprojectBuildingDetails[]>;
  fetchSubprojectDetails: (
    subprojectId: string,
    organizationId?: string,
  ) => Promise<SubprojectBuildingDetails>;
  new: (
    details: Omit<Subproject, 'id'> & { organizationId: string },
  ) => Promise<SubprojectBuildingDetails>;
  update: <T extends Subproject>(details: T) => Promise<T>;
  updateSubprojectBuildingDetails: (
    subproject: SubprojectBuildingDetails,
  ) => Promise<SubprojectBuildingDetails>;
}

// Line 107 in original code
export const useSubprojectStore = create<SubprojectStore>((_set, _get) => ({
  // Prefixed unused set and get
  new: async (details: Omit<Subproject, 'id'> & { organizationId: string }) => {
    try {
      const createdSubproject = await createSubprojectService(details);

      const subprojectDetails = await fetchSubprojectDetails(
        createdSubproject.id,
        details.organizationId,
      );

      return subprojectDetails;
    } catch (e) {
      console.error('Unable to create subproject', e);
      throw e;
    }
  },

  fetchSubprojects: async (projectId: string, organizationId?: string) => {
    try {
      if (!projectId) {
        throw new Error('projectId is undefined');
      }
      // Pass through optional organizationId to service
      return await fetchSubprojects(projectId, organizationId);
    } catch (e) {
      console.error('Unable to load subprojects', e);
      throw e;
    }
  },

  fetchSubprojectDetails: async (
    subprojectId: string,
    organizationId?: string,
  ) => {
    try {
      // Pass through optional organizationId to service
      const subproject = await fetchSubprojectDetails(
        subprojectId,
        organizationId,
      );
      return subproject;
    } catch (e) {
      console.error('Unable to load subproject details', e);
      throw e;
    }
  },

  update: async <T extends Subproject>(details: T) => {
    try {
      // Assuming updateSubprojectService from service returns Promise<T>
      // Add cast if ESLint complains here.
      return await updateSubprojectService(details);
    } catch (e) {
      console.error('Unable to update subproject', e);
      throw e;
    }
  },

  updateSubprojectBuildingDetails: async (
    subproject: SubprojectBuildingDetails,
  ) => {
    try {
      return await updateSubprojectDetailsService(subproject);
    } catch (e) {
      console.error('Unable to update subproject details', e);
      throw e;
    }
  },
}));
